import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Textarea,
  Label,
  toast,
} from "@eduviaos/ui-kit";
import { AlertCircle, Loader2, XOctagon } from "lucide-react";
import { useRejectEnquiry } from "@/hooks/useAdmissions";

/**
 * "Reject this enquiry" confirmation dialog.
 *
 * Reject is destructive in spirit — it moves the enquiry into a
 * terminal stage (`terminal_outcome=rejected`) which makes it
 * disappear from the active funnel — so we require:
 *   1. A non-blank reason (BE enforces too; we shift-left the check).
 *   2. An explicit click on the destructive variant button.
 *
 * The reason flows into `stage_history[-1].note` — surfaced everywhere
 * the timeline renders. Be specific, IA: "wrong vertical", "out of
 * fee bracket", "didn't show for interview" all read better than
 * "not interested" three months from now.
 */

interface EnquiryRejectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  enquiryId: string;
  studentName?: string;
  /** Fired after a successful 200. The list cache is already
   * invalidated; consumers usually navigate away from the detail
   * page or close the surrounding drawer. */
  onRejected?: () => void;
}

export function EnquiryRejectDialog({
  open,
  onOpenChange,
  enquiryId,
  studentName,
  onRejected,
}: EnquiryRejectDialogProps) {
  const reject = useRejectEnquiry(enquiryId);
  const [reason, setReason] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setReason("");
      setError(null);
    }
  }, [open]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = reason.trim();
    if (!trimmed) {
      setError("Please add a short reason — it lands on the audit trail.");
      return;
    }
    try {
      await reject.mutateAsync({ reason: trimmed });
      toast.success(studentName ? `Rejected ${studentName}.` : "Rejected enquiry.");
      onOpenChange(false);
      onRejected?.();
    } catch (err) {
      const e = err as { message?: string };
      setError(e?.message ?? "Couldn't reject the enquiry. Please try again.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <span className="bg-destructive/10 text-destructive inline-flex size-8 items-center justify-center rounded-md">
                <XOctagon className="size-4" aria-hidden="true" />
              </span>
              Reject enquiry
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {studentName ? (
                <>
                  This moves <span className="text-foreground font-medium">{studentName}</span> into
                  the rejected stage. They'll drop off the active funnel but stay searchable in
                  audit history.
                </>
              ) : (
                "This moves the enquiry into the rejected stage. They'll drop off the active funnel but stay searchable in audit history."
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5 py-4">
            <Label htmlFor="reject-reason" className="text-foreground text-xs font-medium">
              Reason for rejection <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="reject-reason"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (error) setError(null);
              }}
              placeholder="e.g. Out of fee bracket; counsellor advised alternate course"
              rows={3}
              required
              maxLength={500}
              autoFocus
              disabled={reject.isPending}
              className="resize-none"
              data-testid="reject-reason-input"
            />
            <div className="text-muted-foreground flex justify-between text-[11px]">
              <span>Visible to anyone who can read this enquiry's history.</span>
              <span className="tabular-nums">{reason.length}/500</span>
            </div>
            {error && (
              <p role="alert" className="text-destructive flex items-start gap-1.5 pt-1 text-xs">
                <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                <span>{error}</span>
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={reject.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={reject.isPending}
              data-testid="reject-confirm"
            >
              {reject.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              ) : (
                <XOctagon className="size-4" aria-hidden="true" />
              )}
              Reject enquiry
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
